import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import LocaleContext from '../context/localeContext';
import { BlogContent, MarkdownContent } from '../components/Content';
import Hero from '../components/Hero';
import Section from '../components/Section';
import BlogRoll from '../components/BlogRoll';
import ContrastContext from '../context/contrastContext';

function DownloadButton({ src, text, className }) {
  const { isHighContrastEnabled } = useContext(ContrastContext);

  let colorClasses = ' bg-red hover:bg-redHover focus:bg-redPressed';
  if (isHighContrastEnabled) {
    colorClasses = ' bg-redHC hover:bg-redHoverHC focus:bg-redPressedHC';
  }

  className = `${
    className == null ? '' : className
  } button block text-center md:inline-block mb-12 px-12 py-3 rounded-full text-white cursor-pointer border border-transparent`;

  className += colorClasses;

  function sendDownloadEvent() {
    if (typeof window.plausible === 'function') {
      window.plausible('Report Download', {
        callback: () => console.info('Sent Report Download event'),
      });
    } else {
      console.warn('Ignoring Report Download Event: localhost');
    }
  }

  return (
    <a
      className={className}
      role="button"
      tabIndex={0}
      onKeyPress={sendDownloadEvent}
      onClick={sendDownloadEvent}
      href={src}
      download
    >
      {text}
    </a>
  );
}

DownloadButton.propTypes = {
  src: PropTypes.string.isRequired,
  text: PropTypes.string,
  className: PropTypes.string,
};

DownloadButton.defaultProps = {
  text: 'Download the report',
};

function formatDownloadSrc(path) {
    return path.replace(/^(\/static)/, '');
}

const ResearchPublication = ({
  title,
  date,
  heroImage,
  downloads,
  takeaways,
  content,
  contentComponent,
  tags,
}) => {
  const { locale } = useContext(LocaleContext);
  const ContentComponent = contentComponent ?? BlogContent;
  const formattedDate = DateTime.fromISO(date)
    .setLocale(locale)
    .toLocaleString(DateTime.DATE_FULL);

  return (
    <>
      <Hero
        image={heroImage}
        content={`${formattedDate} \n## ${title}`}
        autoHeaderText={false}
      />
      {takeaways && (
        <Section colorScheme="black">
          <div className="container py-8">
            <div className="row flex justify-between">
              <span className="bg-theme-underline text-2xl">Key Takeaways</span>
            </div>
            <div className="row py-10 px-12">
                <MarkdownContent content={'\r\n- ' + takeaways.join('\r\n- ')} />
            </div>
          </div>
        </Section>
      )}
      <Section colorScheme="white" overlayLength="large">
        <div className="container py-12">
          <ContentComponent content={content} />
          {downloads ? (
          <Section colorScheme="white">
            <div className="container gap-12 pt-12 m-auto block text-center">
              {downloads.map((download) => (
                <DownloadButton
                  src={formatDownloadSrc(download.path)}
                  text={download.label}
                />
              ))}
            </div>
          </Section>
          ) : null}
          {tags && tags.length ? (
            <div className="mt-2">
              <h4>Tags</h4>
              <ul>
                {tags.map((tag) => (
                  <li key={tag + `tag`}>
                    {/* TODO: Link to some page showing posts with this link? */}
                    <span>{tag}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </Section>
    </>
  );
};

ResearchPublication.propTypes = {
  title: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  heroImage: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  downloadSrc: PropTypes.string,
  takeaways: PropTypes.array,
  content: PropTypes.object.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string),
};

export default ResearchPublication;
